import { StructuredQA } from '../../qa/StructuredQA';
import { QueryExecutor } from '../../qa/QueryExecutor';
import type { IAgentTool } from '../IAgentTool';

export const searchTool: IAgentTool = {
  name: 'search',
  description: 'Search nodes and answer basic questions using structured queries.',
  parameters: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'Question or search term' },
    },
    required: ['query'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'search',

  async execute(params, context) {
    const query = String(params.query ?? '').trim();
    if (!query) return { success: false, summary: 'Missing query.' };

    const structuredQA = new StructuredQA();
    const parsed = structuredQA.tryParse(query);
    const executor = new QueryExecutor();
    const nodes = context.diagramData.nodes.map((node) => ({
      id: node.id,
      type: node.type,
      data: { ...(node.attrs ?? {}), ...(node.props ?? {}), ...(node.name ? { name: node.name } : {}) } as Record<string, unknown>,
    }));
    const edges = context.diagramData.edges.map((edge) => ({
      id: edge.id,
      type: edge.type,
      from: edge.from,
      to: edge.to,
    }));

    const result = parsed
      ? executor.execute(parsed, { nodes, edges })
      : executor.execute({ type: 'findByName', params: [query], originalQuestion: query, confidence: 0.6 }, { nodes, edges });

    if (context.stores.highlight?.setHighlightedIds) {
      context.stores.highlight.setHighlightedIds(new Set(result.nodeIds));
    }

    const matchDetails = result.nodeIds.map((id) => {
      const node = nodes.find((n) => n.id === id);
      if (!node) return id;
      const name = node.data?.['name'] as string | undefined;
      const title = (node.data?.['title'] ?? node.data?.['jobTitle'] ?? node.data?.['role']) as string | undefined;
      const dept = (node.data?.['department'] ?? node.data?.['team']) as string | undefined;
      const parts = [name ?? id, title, dept].filter(Boolean);
      return parts.join(', ');
    });
    const summary = matchDetails.length
      ? `Found ${matchDetails.length} match${matchDetails.length !== 1 ? 'es' : ''} for '${query}': ${matchDetails.join('; ')}`
      : result.message;

    return {
      success: result.success,
      summary,
      data: { nodeIds: result.nodeIds, count: result.count },
    };
  },
};
